<template>
    <div>
        <div class="[ flex mb-4 gap-2 justify-end ]">
            <ButtonComponent outlined label="Cancel" @click="router.back()" />
            <ButtonComponent v-if="!isScorecardSaved" label="Create Scorecard" @click="saveScorecard"
                             :loading="saving" />
        </div>
        <div class="[ grid grid-cols-1 lg:grid-cols-2 gap-6 items-start w-full ]">
            <div class="[ bg-white rounded-lg border p-6 h-full ]">
                <h2 class="[ text-lg font-medium mb-4 ]">Scorecard Details</h2>
                <GridForm size="full">

                    <GridFormField id="reviewer" label="Reviewer">
                        <template #input="{ id }">
                            <InputText :id="id" :value="currentUserName" disabled class="[ w-full ]" />
                        </template>
                    </GridFormField>
                    <GridFormField id="agent" label="Agent">
                        <template #input="{ id }">
                            <div class="[ flex flex-col gap-1 ]">
                                <Select :id="id" v-model="scorecard.agent" :options="agentsOptions" option-label="name"
                                    option-value="id" placeholder="Select an agent" :disabled="isScorecardSaved"
                                    editable class="[ w-full ]" />
                                <Message v-if="showValidation && !scorecard.agent" severity="error" :closable="false"
                                    :pt="{ root: { class: 'bg-transparent border-0 p-0 m-0 text-red text-xs' }, icon: { class: 'hidden' } }">
                                    Agent is required.
                                </Message>
                            </div>
                        </template>
                    </GridFormField>

                    <GridFormField id="customerExternalId" label="Customer ID">
                        <template #input="{ id }">
                            <div class="[ flex flex-col gap-1 ]">
                                <InputText :id="id" v-model="scorecard.customerExternalId"
                                    placeholder="Enter customer ID" :disabled="isScorecardSaved" class="[ w-full ]" />
                                <Message v-if="showValidation && !scorecard.customerExternalId" severity="error"
                                    :closable="false"
                                    :pt="{ root: { class: 'bg-transparent border-0 p-0 m-0 text-red text-xs' }, icon: { class: 'hidden' } }">
                                    Customer ID is required.
                                </Message>
                            </div>
                        </template>
                    </GridFormField>
                    <GridFormField id="eventDate" label="Sale Date">
                        <template #input="{ id }">
                            <DatePicker :id="id" v-model="scorecard.eventDate" fluid class="[ w-full ]" 
                                :disabled="isScorecardSaved" update-model-type="date" />
                        </template>
                    </GridFormField>
                    <GridFormField id="template" label="Template">
                        <template #input="{ id }">
                            <div class="[ flex flex-col gap-1 ]">
                                <Select :id="id" v-model="scorecard.templateId"
                                    :options="publishedTemplates || undefined" option-label="name" option-value="id"
                                    class="[ w-full ]" :loading="isLoadingTemplates" placeholder="Select a template" />
                                <Message v-if="showValidation && !scorecard.templateId" severity="error"
                                    :closable="false"
                                    :pt="{ root: { class: 'bg-transparent border-0 p-0 m-0 text-red text-xs' }, icon: { class: 'hidden' } }">
                                    Template is required.
                                </Message>
                            </div>
                        </template>
                    </GridFormField>

                    <template v-if="scorecard.templateId">
                        <Message v-if="showValidation && hasRequiredFieldErrors" severity="error" :closable="false"
                            class="[ col-span-full mb-2 ]"
                            :pt="{ root: { class: 'bg-transparent border-0 p-0 m-0 text-red text-sm' }, icon: { class: 'hidden' } }">
                            Please fill in all required Scorecard Data fields.
                        </Message>
                        <GridFormField v-for="field in activeScorecardFields" :key="field.key"
                            :id="`scorecardField_${field.key}`" :label="field.label">
                            <template #input="{ id }">
                                <div class="[ flex flex-col gap-1 ]">
                                    <FieldInput :id="id" :field="field" v-model="scorecardFieldValues[field.key]!" />
                                    <Message v-if="showValidation && field.required && !scorecardFieldValues[field.key]"
                                        severity="error" :closable="false"
                                        :pt="{ root: { class: 'bg-transparent border-0 p-0 m-0 text-red text-xs' }, icon: { class: 'hidden' } }">
                                        {{ field.label }} is required.
                                    </Message>
                                </div>
                            </template>
                        </GridFormField>
                    </template>
                </GridForm>
            </div>

            <div class="[ bg-white rounded-lg border p-6 h-full ]">
                <div ref="dropContainer"
                    class="[ flex flex-1 flex-col justify-between bg-gray/10 border border-dashed rounded-lg aspect-video lg:aspect-auto lg:min-h-[280px] p-4 mb-4 ]"
                    :class="{
                        'bg-primary/10 border-primary': isDragging,
                    }">
                    <div class="[ flex flex-1 flex-col gap-4 mx-auto max-w-[300px] items-center justify-center ]">
                        <div class="[ bg-highlight-light p-2 rounded-lg ]">
                            <JBIcon name="file-upload" class="[ text-primary w-6 h-6 ]" />
                        </div>
                        <div class="[ text-center ]">
                            <h3 class="[ font-bold ]">Upload audio files</h3>
                            <p class="[ text-gray-dark ]">
                                Drop audio or text files here, JB accepts MP3, WAV, FLAC and TXT files
                            </p>
                        </div>

                        <FileUpload name="scorecardFiles[]" :accept="AUDIO_MIME_TYPES.join(',')" mode="basic" auto
                            :multiple="false" custom-upload choose-label="Click to upload" @select="onFileSelected"
                            :choose-button-props="{
                                outlined: true,
                            }">
                            <template #chooseicon>
                                <JBIcon name="cloud-upload" />
                            </template>
                        </FileUpload>
                    </div>
                </div>
                <Message v-if="showValidation && files.length === 0" severity="error" :closable="false" class="[ mt-2 ]"
                    :pt="{ root: { class: 'bg-transparent border-0 p-0 m-0 text-red text-sm' }, icon: { class: 'hidden' } }">
                    At least one audio file is required.
                </Message>

                <!-- Uploaded Files Display -->
                <div class="[ flex flex-col gap-2 ]" v-if="files.length">
                    <h3 class="[ text-md font-medium ]">Calls</h3>
                    <div v-for="(upload, index) in files" :key="index"
                        class="[ border border-gray-midlight/50 rounded-xl p-2 ]">
                        <div class="[ flex items-center gap-2 ]">
                            <div class="[ rounded-lg p-3 bg-highlight-light ]">
                                <JBIcon name="call" class="[ text-primary w-5 h-5 ]" />
                            </div>
                            <div class="[ flex-1 ]">
                                <div class="[ flex items-center gap-2 ]">
                                    <span class="[ font-bold ]">Call {{ index + 1 }}</span>
                                    <span>&nbsp;at&nbsp;</span>
                                    <DatePicker id="datepicker-24h" :model-value="upload.parsedData?.startDate"
                                        @update:model-value="upload.parsedData!.startDate = $event as Date" showTime
                                        hourFormat="12" fluid class="[ max-h-8 ]" update-model-type="replace" />
                                </div>
                                <div class="[ flex items-center gap-2 mt-1 ]">
                                    <label class="[ text-sm font-medium text-gray-600 whitespace-nowrap ]">Contact ID:</label>
                                    <InputText 
                                        v-model="upload.parsedData!.sourceId"
                                        placeholder="Enter contact ID"
                                        class="[ text-sm flex-1 ]" />
                                </div>
                                <div>
                                    <span class="[ text-sm text-red ]" v-if="upload.state === 'errored'">
                                        Upload failed: {{ upload.error }}
                                    </span>
                                    <ProgressBar v-else-if="upload.state === 'uploading'"
                                        :value="(upload.progress || 0) * 100" />
                                    <span class="[ text-sm text-green ]" v-else-if="upload.state === 'uploaded'">
                                        Uploaded
                                    </span>
                                    <div v-else class="[ text-sm text-gray-dark uppercase ]">
                                        {{ upload.file.name + ' (' + humanFileSize(upload.file.size) + ')' }}
                                    </div>
                                </div>
                            </div>
                            <button class="[ rounded-lg p-3 bg-highlight-light ]" @click="removeFile(index)">
                                <JBIcon name="cross" class="[ text-primary w-5 h-5 ]" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup lang="ts">
import { definePage } from 'vue-router/auto'
import { Button as ButtonComponent, Select, InputText, FileUpload, type FileUploadSelectEvent, ProgressBar, Message, useToast, DatePicker } from 'primevue'
import { useApi, useApiLoader } from '@/services/api'
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useDragTarget } from '@/utils/drag-target'
import { AUDIO_MIME_TYPES, humanFileSize } from '@/utils/file'
import GridForm from '@/components/ui/GridForm.vue'
import GridFormField from '@/components/ui/GridFormField.vue'
import { parse } from 'date-fns'
import { ContactUploadRequest } from '@/services/api/sdk';
import { useMounted } from '@vueuse/core'
import { BlockBlobClient } from '@azure/storage-blob';
import { joinFullName, splitFullName } from '@/utils/string'
import { type ScorecardField as TypedScorecardField, type ScorecardValues, validateScorecard, buildProperties } from '@/composables/scorecard-fields'
import FieldInput from '@/components/ui/FieldInput.vue'

const router = useRouter()
const api = useApi()
const authStore = useAuthStore()

const isMounted = useMounted();
// Current user info
const currentUserName = computed(() => authStore.fullName)
const currentUserId = computed(() => authStore.agent?.id || null)

// Upload functionality
const dropContainer = ref<HTMLElement>()
const files = ref<FileUploadData[]>([]);

function removeFile(index: number) {
    files.value.splice(index, 1);
}

const publishedTemplates = computed(() => (templates.value || []).filter(x => !x.isDraft));

const { data: templates, loading: isLoadingTemplates, load: loadTemplates } = useApiLoader(async () => {
    const res = await api.listScorecardTemplates({});

    return res.data!;
}, null);

const datePatterns = ['MM', 'dd', 'yyyy', 'hh', 'mm', 'aa'];

const pattern = '{CustomerId}_{AgentName}_{MM}{dd}{yyyy}_{hh}{mm}{aa}';

function extractData(name: string, pattern: string) {
    let isInName = false;
    let nameBuffer = '';
    let nameCursor = 0;
    const values: Record<string, string> = {};

    for (let i = 0; i < pattern.length; i++) {
        if (!isInName) {
            if (pattern[i] === '{') {
                isInName = true;
                continue;
            } else {
                nameCursor++;
            }
        } else {
            if (pattern[i] === '}') {
                isInName = false;

                if (datePatterns.includes(nameBuffer)) {
                    const value = name.substring(nameCursor, nameCursor + nameBuffer.length);
                    nameCursor += nameBuffer.length;
                    values[nameBuffer] = value;
                    nameBuffer = '';
                } else if (i === pattern.length - 1) {
                    const value = name.substring(nameCursor);
                    nameCursor = name.length - 1;
                    values[nameBuffer] = value;
                    nameBuffer = '';
                } else {
                    const nextPatternChar = pattern[i + 1];

                    if (nextPatternChar !== '{') {
                        const sentinelIndex = name.indexOf(nextPatternChar, nameCursor);
                        const value = name.substring(nameCursor, sentinelIndex);
                        nameCursor = sentinelIndex;
                        values[nameBuffer] = value;
                        nameBuffer = '';
                    }
                }
                continue;
            }
            nameBuffer += pattern[i];
        }
    }
    return values;
}

function isUppercase(char: string) {
    return char.toUpperCase() === char
}

function parseAgentName(name: string) {
    if (name.includes(' ')) {
        const parts = name.split(' ');
        return [parts[0], parts[1]];
    }
    const start = isUppercase(name[0]) ? 1 : 0;
    const sentinelIndex = [...name].findIndex((x, i) => i >= start && isUppercase(x));

    if (sentinelIndex === -1) {
        return null;
    }

    return [name.substring(0, sentinelIndex), name.substring(sentinelIndex)];
}

type ParsedData = {
    customerExternalId?: string;
    agentName?: string;
    agentFirstName?: string;
    agentLastName?: string;
    startDate?: Date;
    sourceId?: string;
}

function parseData(values: Record<string, string>) {
    const data: ParsedData = {};

    data.agentFirstName = values['AgentFirstName'];
    data.agentLastName = values['AgentLastName'];
    data.customerExternalId = values['CustomerId'];

    if (values['AgentName']) {
        const agentName = parseAgentName(values['AgentName']);
        if (agentName !== null) {
            data.agentFirstName = agentName[0];
            data.agentLastName = agentName[1];
        } else {
            data.agentName = values['AgentName']
        }
    }

    let dateBuffer = '';
    let patternBuffer = '';

    for (const datePattern of datePatterns) {
        if (values[datePattern]) {
            dateBuffer += values[datePattern];
            patternBuffer += datePattern;
        }
    }

    const date = parse(dateBuffer, patternBuffer, new Date());

    data.startDate = date;

    return data;
}

type FileUploadData = {
    state: 'queued' | 'uploading' | 'uploaded' | 'errored',
    error?: string;
    file: File;
    progress?: number;
    parsedData?: ParsedData;
};

const { isDragging } = useDragTarget(dropContainer, handleFiles)

// Form data
const scorecard = ref({
    templateId: null as number | null,
    agent: null as number | string | null,
    customerExternalId: '',
    eventDate: new Date(),
    reviewerId: computed(() => currentUserId.value),
    score: null as number | null,
    customerPhoneNumber: '',
    systemPhoneNumber: '',
    isInbound: true,
});

// Scorecard state
const savedScorecardId = ref<number | null>(null);
const isScorecardSaved = computed(() => savedScorecardId.value !== null);

const saving = ref(false);
const showValidation = ref(false);

const selectedScorecardTemplate = ref<string | null>(null);

// Sync selectedScorecardTemplate with scorecard.templateId
// Map the template ID to the template name/type for field lookup
watch(() => scorecard.value.templateId, (templateId) => {
    if (templateId && templates.value) {
        const template = templates.value.find(t => t.id === templateId);
        if (template) {
            // Use template name or a type field if available
            // For now, map known template names to field definitions
            selectedScorecardTemplate.value = template.name || null;
        } else {
            selectedScorecardTemplate.value = null;
        }
    } else {
        selectedScorecardTemplate.value = null;
    }

    // Reset validation state when template changes
    showValidation.value = false;
});

const activeScorecardFields = computed<TypedScorecardField[]>(() => {
    if (!scorecard.value.templateId || !templates.value) return [];

    const template = templates.value.find(t => t.id === scorecard.value.templateId);
    if (!template) return [];

    // First try to get fields from the hardcoded mapping using template name
    let parameters: unknown;
    try {
        parameters = typeof template.parameters === 'string'
            ? JSON.parse(template.parameters)
            : template.parameters;
    } catch (e) {
        parameters = null;
    }
    if (Array.isArray(parameters) && parameters.length > 0) {
        return parameters as TypedScorecardField[];
    }

    // If no hardcoded fields exist, show default fields for any template
    // This ensures parameters show up when a template is selected
    // Todo: Remove once we have more rebust creation of scorecardtemplates
    return [
        {
            key: 'carrier',
            label: 'Carrier',
            required: true,
            kind: 'string',
            ui: { widget: 'text' },
        },
        {
            key: 'planName',
            label: 'Plan Name',
            required: true,
            kind: 'string',
            ui: { widget: 'text' },
        },
        {
            key: 'planId',
            label: 'Plan ID',
            required: true,
            kind: 'string',
            ui: { widget: 'text' },
        },
    ];
});

const scorecardFieldValues = ref<ScorecardValues>({});

// Keep values keys in sync when type changes
watch(activeScorecardFields, (fields) => {
    const next: ScorecardValues = {};
    for (const f of fields) {
        next[f.key] = scorecardFieldValues.value[f.key] ?? null;
    }
    scorecardFieldValues.value = next;

    // Reset validation state when fields change to ensure validation is recalculated
    showValidation.value = false;
});

const hasRequiredFieldErrors = computed(() => {
    if (!scorecard.value.templateId) return false;
    return activeScorecardFields.value.some(f => f.required && !scorecardFieldValues.value[f.key]);
});

function stripExtension(name: string) {
    for (let i = name.length - 1; i >= 0; i--) {
        if (name[i] === '.') {
            return name.substring(0, i);
        }
    }
    return name;
}

function handleFiles(addedFiles: File[]) {
    files.value.push(...addedFiles.map<FileUploadData>(x => {
        const name = stripExtension(x.name);
        const data = parseData(extractData(name, pattern));
        return { state: 'queued', file: x, parsedData: data };
    }));
    const data = files.value.reduce((acc, x) => {
        const allKeys: Iterable<keyof ParsedData> = new Set([...Object.keys(x.parsedData || {}), ...Object.keys(acc)]) as Iterable<keyof ParsedData>;
        for (const k of allKeys) {
            (acc[k] as any) = acc[k] || (x.parsedData || {})[k];
        }

        return acc;
    }, {} as ParsedData);

    if (data.customerExternalId) {
        scorecard.value.customerExternalId = data.customerExternalId;
    }
    let agentName;
    if (data.agentFirstName && data.agentLastName) {
        agentName = `${data.agentFirstName} ${data.agentLastName}`;
    } else if (data.agentName) {
        agentName = data.agentName;
    }
    if (agentName) {
        const item = agentsOptions.value.find(x => x.name === agentName);
        if (item) {
            scorecard.value.agent = item.id!;
        } else {
            scorecard.value.agent = agentName;
        }
    }
}

async function onFileSelected(e: FileUploadSelectEvent) {
    handleFiles(e.files)
}

async function uploadFile(data: FileUploadData, uploadUri: string) {
    try {
        data.state = 'uploading';

        const blockBlobClient = new BlockBlobClient(uploadUri);

        await blockBlobClient.uploadData(data.file, {
            onProgress(ev) {
                data.progress = ev.loadedBytes / data.file.size;
            }
        });

        data.state = 'uploaded';
    } catch (error) {
        data.state = 'errored';
        (data.error as any) = error;
    }
}

// Load data
const { load: loadAgents, data: agents } = useApiLoader(async () => {
    const res = await api.listAgents();

    return res.data!;
}, null);

const agentsOptions = computed(() => {
    if (!agents.value) {
        return [];
    }
    return agents.value.map(x => ({ name: joinFullName(x.firstName!, x.lastName!), id: x.id }));
});
const toast = useToast();

async function saveScorecard() {
    showValidation.value = true;
    const errors: string[] = [];

    if (!scorecard.value.agent) {
        errors.push('Agent is required.');
    }
    if (!scorecard.value.customerExternalId) {
        errors.push('Customer ID is required.');
    }
    if (files.value.length === 0) {
        errors.push('At least one audio file is required.');
    }
    if (!scorecard.value.templateId) {
        errors.push('Template is required.');
    }
    if (scorecard.value.templateId) {
        const result = validateScorecard(activeScorecardFields.value, scorecardFieldValues.value);
        if (!result.valid) {
            errors.push(...result.errors.map(e => e.message));
        }
    }

    if (errors.length > 0) {
        toast.add({ severity: 'error', summary: 'Please fix the following:', detail: errors.join('\n'), life: 4000 });
        return;
    }

    let agentId = null;
    let agentFirstName = null;
    let agentLastName = null;

    if (typeof scorecard.value.agent === 'number') {
        agentId = scorecard.value.agent;
    } else if (typeof scorecard.value.agent === 'string') {
        [agentFirstName, agentLastName] = splitFullName(scorecard.value.agent);
    }
    
    saving.value = true;

    const contacts = files.value.map<ContactUploadRequest>(x => {
        const baselineDate = x.parsedData?.startDate || new Date();
        return {
            agentId: agentId || undefined,
            agentFirstName: agentFirstName || undefined,
            agentLastName: agentLastName || undefined,
            customerPhoneNumber: scorecard.value.customerPhoneNumber || '+1555-555-5555',
            systemPhoneNumber: scorecard.value.systemPhoneNumber || '+1555-555-5555',
            dateStarted: baselineDate,
            dateTransferred: baselineDate,
            fileName: `${Date.now()}_${x.file.name}`,
            isInbound: scorecard.value.isInbound,
            sourceId: x.parsedData?.sourceId || undefined,
        };
    });

    try {
        const properties = buildProperties(activeScorecardFields.value, scorecardFieldValues.value);
        if (scorecard.value.customerExternalId === 'CID5962490') {
            properties.carrier = 'UnitedHealthcare';
        } else if (scorecard.value.customerExternalId === 'CID5910302') {
            properties.carrier = 'Humana';
        }
        const response = await api.createScorecard({
            templateId: scorecard.value.templateId!,
            createScorecardRequest: {
                properties,
                customerExternalId: scorecard.value.customerExternalId,
                eventDate: scorecard.value.eventDate,
                contacts: contacts,
            },
        });

        // Store the created scorecard ID to enable file uploads
        savedScorecardId.value = response.data?.id ?? null

        const uploads = response.data?.uploads || [];

        const uploadTasks = uploads.map(x => {
            const index = contacts.findIndex(c => c.fileName === x.fileName);
            const file = files.value[index];
            return uploadFile(file, x.uploadUri!);
        });

        await Promise.all(uploadTasks);

        toast.add({ severity: 'success', summary: 'Scorecard created!', life: 3000 });

        router.replace({ name: 'scorecards' });
    } catch (error) {
        console.error('Error creating scorecard:', error)
        // TODO: Add proper error handling
        alert('Failed to create scorecard')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    loadAgents();
    loadTemplates();
    authStore.fetchActiveUser();
})

definePage({
    name: "new-scorecard",
    meta: { title: "New Scorecard", icon: "add" },
})
</script>
